import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LangSwitch from "@/components/LangSwitch";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import axios from "axios";
import { Plus, FileText, Trash, SignOut, CalendarBlank, ChartBar, House, Lightning, Newspaper, Rows, UsersThree } from "@phosphor-icons/react";
import UncleDaveWidget from "@/components/UncleDaveWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    api.listReports().then(setReports).catch(console.error).finally(() => setLoading(false));
    const token = localStorage.getItem("esg_token");
    axios.get(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSub(r.data)).catch(() => setSub({ active: false }));
    if (searchParams.get("subscription") === "success") {
      toast.success(lang === "ar" ? "اشتراكك مفعَّل! يمكنك الآن إنشاء تقاريرك." : "Subscription active! You can now create reports.");
      searchParams.delete("subscription");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    await api.deleteReport(id);
    setReports(r => r.filter(x => x._id !== id));
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  // Compare EN vs AR requires an inline-PDF-capable viewport (mobile
  // browsers punt to a "Download / Open" prompt that kicks the user
  // out of the SPA). We hide the compare entry pill on narrow screens
  // AND on mobile user agents (some laptops emulate mobile UA in dev
  // tools, and some tablets in landscape have wide viewports but poor
  // iframe-PDF support).
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  const isMobileUA = false; // viewport-only gate; UA check was over-triggering on some laptops
  const hideCompare = isNarrow || isMobileUA;

  const scoreColor = (score) => {
    if (score >= 70) return "text-[#01AF9B]";
    if (score >= 40) return "text-[#F97316]";
    return "text-red-400";
  };

  return (
    <div className="min-h-screen" style={{ background: "#070709" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070709]/70 border-b border-white/[0.06]" data-testid="dashboard-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Logo size="md" />
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <span className="text-slate-400 text-sm hidden sm:block">{user?.name || user?.email}</span>
            <LangSwitch />
            {user?.is_admin && (
              <button onClick={() => navigate("/admin")} className="flex items-center gap-1.5 bg-[#73324B]/20 border border-[#73324B]/40 text-[#D4A0B3] hover:bg-[#73324B]/30 px-3 py-1.5 rounded-lg text-sm font-['Outfit'] font-semibold transition-colors" data-testid="admin-button">
                Admin
              </button>
            )}
            <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300 text-sm transition-colors" data-testid="home-button">
              <House size={18} /> <span className="hidden sm:inline">Home</span>
            </button>
            <button data-testid="logout-button" onClick={handleLogout}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-300 text-sm transition-colors">
              <SignOut size={18} /> <span className="hidden sm:inline">{t('logout')}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {sub && !sub.active && !sub.grandfathered && (
          <div className="mb-6 bg-gradient-to-r from-[#F97316]/15 to-[#01AF9B]/10 border border-[#F97316]/40 rounded-xl p-5 sm:p-6 animate-fade-in-up" data-testid="paywall-banner">
            <div className="flex items-start gap-4 flex-wrap">
              <Lightning size={26} weight="fill" className="text-[#F97316] flex-shrink-0 mt-1" />
              <div className="flex-1 min-w-[240px]">
                <h3 className="font-['Outfit'] text-base sm:text-lg font-bold text-white mb-1">
                  {sub.free_available
                    ? (lang === "ar" ? "جرّب معاينة تقرير ESG المجانية" : "Try your free ESG report preview")
                    : (lang === "ar" ? "اشترك لفتح التنزيلات وتقارير غير محدودة" : "Subscribe to unlock downloads & unlimited reports")}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {sub.free_available
                    ? (lang === "ar"
                        ? `اضغط "تقرير جديد" لبدء تعبئة نموذج التقرير المجاني. أكمل الأقسام الخمسة بنسبة 100٪ ثم اضغط "إنشاء التقرير"؛ في أقلّ من 10 دقائق تحصل على نسخة تمهيدية مجانية من 8 صفحات باللغة الإنجليزية، مع علامة مائية. اشترك بـ ${sub.next_charge_amount} $ / سنة لفتح التقرير الكامل بأكثر من 60 صفحة باللغتين الإنجليزية والعربية، مع تقارير وتعديلات غير محدودة طوال العام.`
                        : `Click "New Report" to start the free filling workflow. Complete all 5 sections to 100% and hit "Generate Report"; in under 10 minutes you'll get a free 8-page watermarked English Preview Edition. Subscribe for $${sub.next_charge_amount}/year to unlock the full 60+ page bilingual (EN + AR) report, with unlimited generations and amendments for the year.`)
                    : (lang === "ar"
                        ? `الخطّة السنوية ${sub.next_charge_amount} $, وصول غير محدود، باللغتين العربية والإنجليزية. ${sub.next_charge_amount === 499 ? "سعر العرض حتى 31 أكتوبر 2026." : ""}`
                        : `$${sub.next_charge_amount}/year, unlimited reports, English & Arabic. ${sub.next_charge_amount === 499 ? "Launch price until Oct 31, 2026." : ""}`)}
                </p>
              </div>
              <button onClick={() => navigate("/billing")} data-testid="paywall-banner-cta"
                className="bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-bold text-sm py-2.5 px-5 rounded-lg whitespace-nowrap">
                {lang === "ar" ? "اشترك الآن" : "Subscribe now"}
              </button>
            </div>
          </div>
        )}

        {/* Hero, heading on left, ESG World News button top-right (per draft alignment) */}
        <div className="mb-8 animate-fade-in-up flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-['Outfit'] text-xs font-semibold tracking-[0.2em] uppercase text-[#01AF9B] mb-3 flex items-center gap-2">
              <span className="w-6 h-[2px] bg-[#01AF9B] inline-block"></span> {t('dashboard')}
            </div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-2">{t('your_reports')}</h1>
            <p className="text-slate-400 text-sm">{t('manage_reports')}</p>
          </div>
          <div className="flex flex-col gap-3 self-start flex-shrink-0">
            <button data-testid="esg-news-button" onClick={() => {
                // ESG News is open to freemium users too — only block if they
                // are truly non-paying AND have already burned their 1 free preview.
                if (sub && !sub.active && !sub.grandfathered && !sub.free_available) {
                  navigate("/billing"); return;
                }
                navigate("/news");
              }}
              className="w-full bg-[#EAB308] hover:bg-[#FACC15] text-[#0B0F1A] font-['Outfit'] font-semibold py-3 px-5 rounded-xl transition-all flex items-center gap-2 justify-center shadow-[0_6px_20px_rgba(234,179,8,0.30)]"
              title={t('esg_news_tooltip')}>
              <Newspaper size={18} weight="fill" />
              <span>{t('esg_news')}</span>
            </button>
            <button data-testid="esg-advisors-button" onClick={() => navigate("/professionals")}
              className="w-full bg-[#F97316] hover:bg-[#EA630C] text-white font-['Outfit'] font-semibold py-3 px-5 rounded-xl transition-all flex items-center gap-2 justify-center">
              <UsersThree size={18} weight="fill" />
              <span>{t('nav_advisors')}</span>
            </button>
          </div>
        </div>

        {/* New Report Button */}
        <button data-testid="new-report-button" onClick={() => {
            // Free users can enter the form and fire their 1 free preview.
            // Only redirect to billing if they've already used it.
            if (sub && !sub.active && !sub.grandfathered && !sub.free_available) {
              navigate("/billing"); return;
            }
            navigate("/report/new");
          }}
          className="mb-8 bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-3 px-6 rounded-xl transition-all flex items-center gap-2 animate-fade-in-up stagger-1">
          <Plus size={20} weight="bold" /> {t('new_report')}
        </button>

        {/* Reports Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-20 animate-fade-in-up stagger-2" data-testid="empty-state">
            <FileText size={48} className="text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg mb-2">{t('no_reports')}</p>
            <p className="text-slate-500 text-sm">{t('no_reports_desc')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
            {reports.map((report, i) => (
              <div key={report._id}
                className={`bg-[#111216] border border-slate-800/60 rounded-xl p-5 hover:border-slate-700 transition-all cursor-pointer group animate-fade-in-up stagger-${Math.min(i + 1, 4)}`}
                onClick={() => report.status === "generated" ? navigate(`/report/${report._id}`) : navigate(`/report/${report._id}/edit`)}
                data-testid={`report-card-${report._id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#01AF9B]/10 border border-[#01AF9B]/20 flex items-center justify-center">
                    <FileText size={20} className="text-[#01AF9B]" />
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(report._id); }}
                    className="text-slate-600 hover:text-red-400 transition-colors p-1" data-testid={`delete-report-${report._id}`}>
                    <Trash size={16} />
                  </button>
                </div>
                <h3 className="font-['Outfit'] font-semibold text-white mb-1 group-hover:text-[#01AF9B] transition-colors">
                  {report.company_data?.name || "Untitled Report"}
                </h3>
                <p className="text-slate-500 text-xs mb-3">{report.company_data?.sector || "No sector"} &bull; {report.company_data?.year || "-"}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <ChartBar size={14} className="text-slate-500" />
                    <span className={`font-['Outfit'] text-sm font-bold ${scoreColor(report.scores?.overall || 0)}`}>
                      {report.scores?.overall || 0}%
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                    <CalendarBlank size={12} />
                    <span>{report.created_at ? new Date(report.created_at).toLocaleDateString() : "-"}</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span className={`text-xs font-['Outfit'] font-semibold px-2 py-0.5 rounded-full ${
                    report.status === "generated" ? "bg-[#01AF9B]/10 text-[#01AF9B]" : "bg-[#F97316]/10 text-[#F97316]"
                  }`}>{report.status === "generated" ? t('complete') : t('draft')}</span>
                  {report.status === "generated" && report.has_en && report.has_ar && !hideCompare && (sub && (sub.active || sub.grandfathered)) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/report/${report._id}/compare`); }}
                      className="ml-auto flex items-center gap-1 text-[10px] font-['Outfit'] font-semibold text-[#3B82F6] hover:text-white border border-[#3B82F6]/30 hover:bg-[#3B82F6]/10 px-2 py-0.5 rounded-full transition-colors"
                      title="Compare English and Arabic side-by-side"
                      data-testid={`compare-report-${report._id}`}
                    >
                      <Rows size={11} weight="bold" /> EN &sdot; AR
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-white/[0.06] py-8 px-6 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size="sm" />
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 text-sm text-slate-500">
            <a href="mailto:info@architaly.green" className="hover:text-[#01AF9B] transition-colors">info@architaly.green</a>
            <span>Delaware, USA</span>
            <button onClick={() => navigate("/terms")} className="hover:text-[#01AF9B] transition-colors">Terms</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-[#01AF9B] transition-colors">Privacy</button>
            <button onClick={() => navigate("/refund")} className="hover:text-[#01AF9B] transition-colors">Refunds</button>
          </div>
          <p className="text-xs text-slate-600">&copy; 2026 ESG Report Builder Corp. All rights reserved.</p>
        </div>
      </footer>

      <UncleDaveWidget />
    </div>
  );
}
